import React from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import AccessKeysGuardLog from "../../components/AccessKeysGuardLog";
import "./logs.css";
import { selectUser } from "../../slices/user";
import { showError } from "../../utils/helpers";
import api from "../../api";
import RegisterGuardLog from "../../components/RegisterGuardLog";
import RegisterLastLogs from "../../components/RegisterLastLogs/RegisterLastLogs";
import { AccessGroupsData } from "../../api/models/access-groups";
import { AccessData } from "../../api/models/access-model";
import { Context } from "../../context/ContextProvider/ContextProvider";
import DesktopDropView from "../../components/DesktopDropView";
import DesktopRegisterDetails from "../../components/DesktopRegisterDetails";

export interface AccessGroupCurrent {
  accessGroupId: number;
  accessPointId: number;
  daysAllowed: string[];
  antipassback: number;
  register: boolean;
  photographs: string[];
  fromHour: string;
  toHour: string;
  exitRestriction?: string | null;
  exitRestrictionFrom?: string | null;
  exitRestrictionTo?: string | null;
  roles: number[];
  printable_pass: boolean;
}

const Logs = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const { selectedAccessPointGuard } = React.useContext(Context);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [accessGroups, setAccessGroups] = React.useState<
    AccessGroupsData[] | null
  >(null);
  const [accesses, setAccesses] = React.useState<AccessData[] | null>(null);
  const [accessesPage, setAccessesPage] = React.useState<number>(0);
  const [totalAccess, setTotalAccess] = React.useState<number>(0);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [showAccessDetails, setShowAccessDetails] =
    React.useState<boolean>(false);
  const [selectedAccessId, setSelectedAccessId] = React.useState<string | null>(
    null,
  );

  const accessesPageSize = 15;

  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  React.useEffect(() => {
    if (selectedAccessPointGuard) {
      getAccessGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccessPointGuard]);

  const getAccessGroups = async () => {
    if (selectedAccessPointGuard) {
      try {
        const response = await api.accessGroupsByAccessPointRequest(
          Number(selectedAccessPointGuard),
        );
        if (response) {
          setAccessGroups(response?.data ?? []);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  React.useEffect(() => {
    getAccesses(debouncedSearchFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedAccessPointGuard, debouncedSearchFilter, accessesPage]);

  React.useEffect(() => {
    setAccessesPage(0);
  }, [selectedAccessPointGuard, debouncedSearchFilter]);

  const getAccesses = async (debouncedText: string) => {
    if (user && selectedAccessPointGuard) {
      try {
        setIsLoading(true);
        const response = await api.getAccessesRequest(
          user?.property?.id,
          "updatedAt",
          accessesPage + 1,
          accessesPageSize,
          selectedAccessPointGuard !== "0"
            ? Number(selectedAccessPointGuard)
            : undefined,
          debouncedText,
        );
        if (response) {
          const fixedResponse = response?.data.map((item) => item);
          console.log("fixedResponse", fixedResponse);
          setAccesses(fixedResponse);
          setTotalAccess(response?.meta?.pagination?.total || 0);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="w-100">
      <DesktopHeader title={labels.log} isGuard={true} />
      <DesktopBody hasFilters={false} filterData={null}>
        <div className="w-100 border-box logs-body-container">
          <div className="w-100 h-100 px-8 pb-8 border-box flex justify-content-between gap-6">
            <div
              className="w-35 logs-containers"
              style={{ overflow: "hidden" }}
            >
              <AccessKeysGuardLog
                selectedAccessPoint={
                  selectedAccessPointGuard !== "0"
                    ? Number(selectedAccessPointGuard)
                    : null
                }
              />
            </div>
            <div
              className="w-50 logs-containers"
              style={{ overflow: "hidden" }}
            >
              <RegisterGuardLog
                selectedAccessPoint={
                  selectedAccessPointGuard !== "0"
                    ? Number(selectedAccessPointGuard)
                    : null
                }
                accessGroups={accessGroups}
                exFunction={() => getAccesses(debouncedSearchFilter)}
              />
            </div>
            <div
              className="w-35 logs-containers"
              style={{ overflow: "hidden" }}
            >
              <RegisterLastLogs
                isLoading={isLoading}
                accesses={accesses}
                getAccesses={() => getAccesses(debouncedSearchFilter)}
                searchValue={searchFilter}
                setSearchValue={setSearchFilter}
                setShowAccessDetails={setShowAccessDetails}
                setSelectedAccessId={setSelectedAccessId}
                accessesPage={accessesPage}
                setAccessesPage={setAccessesPage}
                accessesPageSize={accessesPageSize}
                totalAccess={totalAccess}
              />
            </div>
          </div>
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={showAccessDetails}
        setShowView={setShowAccessDetails}
        content={
          <div className="flex h-100 border-box" style={{ width: 500 }}>
            <div className="w-100 h-100 border-box p-8 scroll">
              <DesktopRegisterDetails
                selectedAccessId={selectedAccessId}
                isMobile={false}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={labels.visit_details}
        onCLoseFunc={() => setSelectedAccessId(null)}
      />
    </div>
  );
};

export default Logs;
